import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { CLAIMS_BASE, claimKeys } from './ownership-claims.query';
import type { ClaimAction } from '@/client/types/ownership-claims';

type ClaimActionVars = { claimid: number; action: ClaimAction; note?: string };
type ClaimNoteVars = { claimid: number; note: string };

// JSON bodies: `admin/claims/*` is on the proxy's JSON allowlist (src/server/lib/utils.ts).
export const useClaimActionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  return useMutation<unknown, Error, ClaimActionVars>({
    mutationFn: withErrorHandling(
      async ({ claimid, action, note }: ClaimActionVars) =>
        request<unknown>(`${CLAIMS_BASE}/${claimid}/${action}`, 'POST', { note: note?.trim() || undefined }),
      'Could not update this claim',
    ),
    onSuccess: (_data, { claimid }) => {
      qc.invalidateQueries({ queryKey: claimKeys.lists() });
      qc.invalidateQueries({ queryKey: claimKeys.detail(claimid) });
    },
  });
};

export const useAddClaimNoteMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  return useMutation<unknown, Error, ClaimNoteVars>({
    mutationFn: withErrorHandling(
      async ({ claimid, note }: ClaimNoteVars) =>
        request<unknown>(`${CLAIMS_BASE}/${claimid}/notes`, 'POST', { note }),
      'Could not add the note',
    ),
    onSuccess: (_data, { claimid }) => qc.invalidateQueries({ queryKey: claimKeys.detail(claimid) }),
  });
};
